import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import AnnouncementModal from './AnnouncementModal';
import { Link } from 'react-router-dom';
import { API_CONFIG } from '../apiConfig';
import styles from '../css/Home.module.css';
import CalendarSection from './CalendarSection';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

const CLOUD_NAME = "dvxhzufjh";
const TAG_GALERIA = "carrusel";

const getYouTubeId = (input) => {
    if (!input) return null;
    let url = input;
    if (input.includes('<iframe')) {
        const srcMatch = input.match(/src=["']([^"']+)["']/);
        if (srcMatch && srcMatch[1]) {
            url = srcMatch[1];
        }
    }
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
        return match[2];
    }
    const embedParts = url.split('/embed/');
    if (embedParts.length > 1) {
        const cleanPart = embedParts[1].split('?')[0];
        if (cleanPart.length === 11) {
            return cleanPart;
        }
    }
    return null;
};

const getEmbedUrl = (input) => {
    if (!input) return '';
    if (input.includes('<iframe')) {
        const srcMatch = input.match(/src=["']([^"']+)["']/);
        if (srcMatch && srcMatch[1]) {
            return srcMatch[1];
        }
    }
    return input;
};

const Home = () => {
    const [images, setImages] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [leccion, setLeccion] = useState({ tema: "Cargando lección...", leccion: "", texto: "" });
    const [transmision, setTransmision] = useState(null);
    const [diasConAvisos, setDiasConAvisos] = useState([]);
    const [todosLosAvisos, setTodosLosAvisos] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoId, setVideoId] = useState(null);
    const [thumbUrl, setThumbUrl] = useState('');

    const fetchTransmision = useCallback(async () => {
        try {
            const url = API_CONFIG.TRANSMISION_RECIENTE || `${API_CONFIG.BASE_URL}/api/transmision-reciente/`;
            const response = await axios.get(url);
            setTransmision(response.data);
        } catch (error) { console.error("Error al cargar la transmisión:", error); }
    }, []);

    const fetchLeccion = useCallback(async () => {
        try {
            const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, "");
            const res = await axios.get(`${baseUrl}/api/lecciones/proxima/`);
            setLeccion(res.data);
        } catch (error) {
            setLeccion({ tema: "Próxima Lección", leccion: "Disponible pronto", texto: "Disponible pronto" });
        }
    }, []);

    // BORRA cualquier otra definición de 'reAgruparAvisos' que tengas arriba
    const reAgruparAvisos = (listaAvisos) => {
        const mapa = listaAvisos.reduce((acc, aviso) => {
            const fecha = aviso.fecha_dia;
            if (!acc[fecha]) {
                acc[fecha] = {
                    fecha_dia: fecha,
                    dia_semana_display: aviso.dia_semana_display,
                    avisos: []
                };
            }
            acc[fecha].avisos.push(aviso);
            return acc;
        }, {});
        return Object.values(mapa);
    };

    const fetchData = useCallback(async () => {
        try {
            const [resAvisos, resImages] = await Promise.all([
                axios.get(API_CONFIG.AVISOS_SEMANA),
                fetch(`https://res.cloudinary.com/${CLOUD_NAME}/image/list/${TAG_GALERIA}.json`).then(res => res.json())
            ]);

            const ahora = new Date();

            // 1. Extraer TODOS los avisos individuales de todos los días recibidos [cite: 109, 117]
            let listaGlobalAvisos = [];

            (resAvisos.data.dias || []).forEach(dia => {
                if (dia.fecha_dia && dia.avisos) {
                    dia.avisos.forEach(aviso => {
                        const horaAviso = aviso.hora ? aviso.hora : "23:59:00";
                        const fechaHoraAviso = new Date(`${dia.fecha_dia}T${horaAviso}`);

                        // Solo consideramos avisos que no han pasado la hora actual
                        if (fechaHoraAviso >= ahora) {
                            listaGlobalAvisos.push({
                                ...aviso,
                                fecha_dia: dia.fecha_dia,
                                dia_semana_display: dia.dia_semana_display,
                                fechaHoraObj: fechaHoraAviso
                            });
                        }
                    });
                }
            });

            // 2. Ordenar cronológicamente (el más inminente primero)
            listaGlobalAvisos.sort((a, b) => a.fechaHoraObj - b.fechaHoraObj);

            // 3. Aplicar lógica de visualización según dispositivo [cite: 132]
            const esMovil = window.innerWidth <= 768;
            const agrupadosAvisos = reAgruparAvisos(listaGlobalAvisos);

            setTodosLosAvisos(agrupadosAvisos);

            if (esMovil) {
                // En móvil mostramos los 2 primeros días con avisos para mantener el scroll limpio [cite: 132]
                setDiasConAvisos(agrupadosAvisos.slice(0, 2));
            } else {
                // En PC mostramos hasta los 3 días más próximos, pero TODOS los avisos de cada día
                setDiasConAvisos(agrupadosAvisos.slice(0, 3));
            }

            setImages(resImages.resources || []);

        } catch (error) {
            console.error("Error en carga de datos:", error);
        }
    }, []);

    useEffect(() => {
        fetchData();
        fetchLeccion();
        fetchTransmision();

        // Opcional: Refrescar cada 5 minutos para rotar avisos automáticamente
        const interval = setInterval(fetchData, 300000);
        return () => clearInterval(interval);
    }, [fetchData, fetchLeccion, fetchTransmision]);

    useEffect(() => {
        if (transmision && transmision.url_iframe) {
            const id = getYouTubeId(transmision.url_iframe);
            setVideoId(id);
            if (id) {
                setThumbUrl(`https://img.youtube.com/vi/${id}/maxresdefault.jpg`);
            }
            setIsPlaying(false);
        }
    }, [transmision]);

    const formatDayAndMonthName = (dateString) => {
        if (!dateString) return '';
        const parts = dateString.split('-');
        const monthIndex = parseInt(parts[1], 10) - 1;
        const day = parts[2];
        const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        return `${day} de ${MONTH_NAMES[monthIndex]}`;
    };

    return (
        <div className={styles.mainContainer}>
            <header className={styles.heroSection}>
                <div className={styles.heroOverlay}></div>
                <div className={styles.heroContent}>
                    <div className={styles.avisosPanelFull}>
                        <div className={styles.cardHeaderMinimal}>
                            <div className={styles.titleWrapper}>
                                <i className="fa-light fa-calendar-star"></i>
                                <h3>Avisos de la Semana</h3>
                            </div>
                        </div>

                        {/* Contenedor con scroll interno solo si es estrictamente necesario */}
                        <div className={styles.scrollAreaAvisos}>
                            <div className={styles.avisosGridInternal}>
                                {diasConAvisos.map((dia, index) => (
                                    <div key={index} className={styles.avisoSubCard}>
                                        <div className={styles.diaHeaderMini}>
                                            <span className={styles.diaNombreMini}>{dia.dia_semana_display}</span>
                                            <span className={styles.diaFechaMini}>{formatDayAndMonthName(dia.fecha_dia)}</span>
                                        </div>
                                        {dia.avisos.map((aviso, i) => (
                                            <div key={i} className={styles.itemAvisoIndividual}>
                                                <strong className={styles.tituloAvisoMini}>{aviso.titulo}</strong>
                                                <div className={styles.badgesAvisoMini}>
                                                    {aviso.hora && <span className={styles.badgeMini}><i className="fas fa-clock"></i> {aviso.hora.substring(0, 5)}</span>}
                                                    {aviso.lugar && <span className={styles.badgeMini}><i className="fas fa-location-dot"></i> {aviso.lugar}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* BANNER INFERIOR MINIMALISTA - ESCUELA DOMINICAL */}
                        <a
                            href="https://escuela.iumpconcon.cl/"
                            target="_blank"
                            className={styles.escuelaDominicalFullCard}
                            rel="noreferrer noopener"
                            aria-label="Acceso directo a Escuela Dominical, se abrirá en una nueva pestaña"
                        >
                            <div className={styles.dataEscuelaMini}>
                                <span className={styles.escuelaBadge}>Acceso directo</span>
                                <span className={styles.labelEscuela}>ESCUELA DOMINICAL</span>
                                <h4 className={styles.temaEscuelaMini}>{leccion.tema}</h4>

                                {/* Contenedor Meta con separación mejorada */}
                                <div className={styles.metaEscuelaMini}>
                                    <div className={styles.metaItem}>
                                        <strong>L:</strong> {leccion.leccion}
                                    </div>
                                    <span className={styles.metaSeparator}>•</span>
                                    <div className={styles.metaItem}>
                                        <strong>T:</strong> {leccion.texto}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.btnWrapper}>
                                <div className={styles.btnAppMini}>
                                    IR A ESCUELA DOMINICAL <i className="fa-light fa-arrow-up-right-from-square"></i>
                                </div>
                                <span className={styles.escuelaDirectoNote}>
                                    Abre el sistema Escuela Dominical en otra pestaña
                                </span>
                            </div>
                        </a>
                    </div>
                </div>
            </header>

            <main className={styles.contentWrapper}>
                {/* CALENDARIO DE ACTIVIDADES */}
                <CalendarSection avisosSemana={todosLosAvisos} />

                {/* 5. ECOSISTEMA */}
                <section className={styles.ecosistemaContainer}>
                    <h2 className={styles.sectionTitle}>Ecosistema Digital</h2>
                    <div className={styles.cardsGrid}>
                        <article className={styles.card}>
                            <div className={styles.cardBackground} style={{ backgroundImage: "url('https://res.cloudinary.com/dvxhzufjh/image/upload/v1775874339/ufpc_overlay_i2pncr.png')" }}></div>
                            <div className={styles.cardOverlay}></div>
                            <a href="https://ufpc.iumpconcon.cl" target="_blank" rel="noreferrer" className={styles.cardContent}>
                                <span className={styles.cardCategory}>Infantil</span>
                                <h3 className={styles.cardTitle}>Un Fruto para Cristo</h3>
                            </a>
                        </article>
                        <article className={styles.card}>
                            <div className={styles.cardBackground} style={{ backgroundImage: "url('https://res.cloudinary.com/dvxhzufjh/image/upload/v1775874348/tutoriales_overlay_iulqqk.png')" }}></div>
                            <div className={styles.cardOverlay}></div>
                            <a href="https://tutoriales.iumpconcon.cl" target="_blank" rel="noreferrer" className={styles.cardContent}>
                                <span className={styles.cardCategory}>Instrumentos</span>
                                <h3 className={styles.cardTitle}>Tutoriales Coro</h3>
                            </a>
                        </article>
                        <article className={styles.card}>
                            <div className={styles.cardBackground} style={{ backgroundImage: "url('https://res.cloudinary.com/dvxhzufjh/image/upload/v1775875220/iump_overlay_kau7i5.png')" }}></div>
                            <div className={styles.cardOverlay}></div>
                            <a href="https://iglesiaunida.cl" target="_blank" rel="noreferrer" className={styles.cardContent}>
                                <span className={styles.cardCategory}>Corporación</span>
                                <h3 className={styles.cardTitle}>Iglesia Unida</h3>
                            </a>
                        </article>
                    </div>
                </section>

                {/* 6. CARRUSEL VERTICAL */}
                <div style={{ marginTop: '80px' }}>
                    <h2 className={styles.sectionTitle}>Momentos de Bendición</h2>
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
                        effect={'coverflow'}
                        grabCursor={true}
                        centeredSlides={true}
                        slidesPerView={'auto'}
                        autoplay={{ delay: 4000 }}
                        coverflowEffect={{ rotate: 0, stretch: 0, depth: 100, modifier: 1, slideShadows: false }}
                        style={{ padding: '50px 0' }}
                    >
                        {images.map((img) => (
                            <SwiperSlide key={img.public_id} style={{ width: '300px' }}>
                                {/* Envolvemos con Link hacia la ruta /galeria */}
                                <Link to="/galeria" style={{ textDecoration: 'none' }}>
                                    <div className={styles.swiperImageWrapper}>
                                        <div className={styles.swiperOverlay}>
                                            <span>Ver Galería</span>
                                        </div>
                                        <img
                                            src={`https://res.cloudinary.com/${CLOUD_NAME}/image/upload/f_auto,q_auto,w_800/v${img.version}/${img.public_id}.${img.format}`}
                                            alt="Iglesia IUMP"
                                        />
                                    </div>
                                </Link>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {/* SECCIÓN ÚLTIMA REUNIÓN */}
                {transmision && (
                    <section className={styles.videoSectionWrapper}>
                        <div className={styles.videoHeader}>
                            <h2 className={styles.sectionTitle}>Última Reunión</h2>
                            <div className={styles.videoStatus}>
                                <span className={styles.pulseDot}></span>
                                Disponible ahora
                            </div>
                        </div>

                        <div className={styles.videoMainCard}>
                            <div className={styles.videoFrameSide}>
                                {!isPlaying && videoId ? (
                                    <div
                                        className={styles.thumbnailCover}
                                        onClick={() => setIsPlaying(true)}
                                    >
                                        <img
                                            src={thumbUrl}
                                            onError={() => {
                                                if (thumbUrl && thumbUrl.includes('maxresdefault')) {
                                                    setThumbUrl(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
                                                }
                                            }}
                                            alt={transmision.titulo}
                                            className={styles.thumbnailImg}
                                        />
                                        <div className={styles.thumbnailOverlay}>
                                            <button className={styles.playButton} aria-label="Reproducir transmisión">
                                                <i className="fas fa-play"></i>
                                            </button>
                                            <div className={styles.thumbnailActions} onClick={(e) => e.stopPropagation()}>
                                                <a
                                                    href={`https://www.youtube.com/watch?v=${videoId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.watchOnYoutubeBtn}
                                                >
                                                    <i className="fab fa-youtube"></i> Ver en YouTube
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <iframe
                                        src={`${getEmbedUrl(transmision.url_iframe)}${getEmbedUrl(transmision.url_iframe).includes('?') ? '&' : '?'}autoplay=1`}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allowFullScreen
                                    ></iframe>
                                )}
                            </div>

                            <div className={styles.videoContentSide}>
                                <div className={styles.videoText}>
                                    <span className={styles.videoDate}>Culto de Adoración</span>
                                    <h3>{transmision.titulo}</h3>
                                    <p>{transmision.subtitulo}</p>
                                </div>

                                <div className={styles.videoLinksContainer}>
                                    {videoId && (
                                        <a
                                            href={`https://www.youtube.com/watch?v=${videoId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.videoLink}
                                        >
                                            Ver en YouTube <i className="fab fa-youtube"></i>
                                        </a>
                                    )}
                                    <a
                                        href="https://www.youtube.com/@iumpconcon5792"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.videoLink}
                                    >
                                        Explorar canal oficial <i className="fas fa-external-link-alt"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>
                )}


            </main>

            {/* 7. MODAL DE AVISOS (RENDERIZADO GLOBAL) */}

            <AnnouncementModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
};

export default Home;